import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import Keycloak from 'keycloak-js';
import { KeycloakGroupSummaryResponse } from '@mobiletract/auth-service-angular-client';
import { Observable, from, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { normalizeTokenGroupNames } from '../auth/token-group-names.util';
import { GroupCacheInterceptor } from '../../shared/interceptors/group-cache.interceptor';

const AUTH_API_URL = `${environment.apiUrl.replace(/\/$/, '')}/uaa`;

@Injectable({
  providedIn: 'root',
})
export class PackageDisplayService {

  private displayNameMap: Record<string, string> = {};

  private readonly keycloak = inject(Keycloak);
  private readonly http = inject(HttpClient);
  private readonly groupCacheInterceptor = inject(GroupCacheInterceptor);

  invalidateCache(): void {
    this.displayNameMap = {};
    this.groupCacheInterceptor.clearAllCaches();
  }

  isLoggedIn(): boolean {
    return this.keycloak?.authenticated ?? false;
  }

  getPackageDisplayName(): string {
    return this.resolveDisplayName();
  }

  getHasGroups$(): Observable<boolean> {
    if (!this.isLoggedIn()) {
      return of(false);
    }
    return this.loadGroupsFromApi().pipe(
      map((response) => (response ?? []).length > 0)
    );
  }

  getPackageDisplayName$(): Observable<string | null> {
    if (!this.isLoggedIn()) {
      return of(null);
    }
    return this.loadGroupsFromApi().pipe(
      map(() => this.resolveDisplayName())
    );
  }

  login(): void {
    this.keycloak.login({
      redirectUri: window.location.href,
    });
  }

  private loadGroupsFromApi(): Observable<KeycloakGroupSummaryResponse[]> {
    return from(this.waitForAccessToken()).pipe(
      switchMap(() =>
        this.http.get<KeycloakGroupSummaryResponse[]>(`${AUTH_API_URL}/group`)
      ),
      take(1),
      tap((response) => this.populateDisplayNameMap(response ?? [])),
      catchError((error) => {
        console.error('PackageDisplayService: failed to load /uaa/group', error);
        return of([]);
      })
    );
  }

  private populateDisplayNameMap(list: KeycloakGroupSummaryResponse[]): void {
    this.displayNameMap = {};
    list.forEach((g) => {
      const display = g.displayName ?? g.name;
      this.displayNameMap[g.name] = display;
      if (g.path) {
        this.displayNameMap[g.path.replace(/^\//, '')] = display;
      }
    });
  }

  private async waitForAccessToken(): Promise<void> {
    const timeoutMs = 10_000;
    const pollMs = 50;
    const startedAt = Date.now();

    while (!this.keycloak?.token && Date.now() - startedAt < timeoutMs) {
      await new Promise((resolve) => setTimeout(resolve, pollMs));
    }
  }

  private resolveDisplayName(): string {
    const groups = normalizeTokenGroupNames(
      (this.keycloak?.tokenParsed as { groups?: string[] } | undefined)?.groups
    );
    for (const name of groups) {
      const directMatch = this.displayNameMap[name];
      if (directMatch) {
        return directMatch;
      }
      const segments = name.split('/').filter(Boolean);
      for (let i = segments.length - 1; i >= 0; i--) {
        const segmentMatch = this.displayNameMap[segments[i]];
        if (segmentMatch) {
          return segmentMatch;
        }
      }
    }
    return '';
  }
}
